// src/hooks/useInvites.js
import { useState, useEffect } from "react";
import { message, Modal } from "antd";
import { fetchInvites, resendInvite, sendInvite } from "PlatForms/OrgLMS/Services/AdminServices/InviteUsersService";

export const useInvites = (selectedRole) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
  });

  const getInvites = async (page = 1) => {
    try {
      setLoading(true);
      const data = await fetchInvites(selectedRole, { pageNumber: page, pageSize: pagination.pageSize });
      setInvites(data.items || []);
      setPagination({
        totalCount: data.totalCount,
        pageNumber: data.pageNumber,
        pageSize: data.pageSize,
        totalPages: data.totalPages,
      });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to fetch invites");
      message.error("Failed to fetch invites");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    getInvites(page);
  };

const handleResend = async (inviteId) => {
  setResendingId(inviteId);
  
  Modal.confirm({
    title: "Are you sure you want to resend the invitation?",
    content: "This will resend the invitation to the user.",
    okText: "Resend",
    cancelText: "Cancel",
    onOk: async () => {
      try {
        // Optimistically update the timestamp before API call
        setInvites(prev => prev.map(invite => 
          invite.id === inviteId 
            ? { ...invite, inviteSentAt: new Date().toISOString() } 
            : invite
        ));

        const result = await resendInvite(inviteId);
        
        // Final update with server response
        setInvites(prev => prev.map(invite => 
          invite.id === inviteId 
            ? { ...invite, ...result.data } 
            : invite
        ));
        
        message.success("Invitation resent successfully!");
      } catch (error) {
        // Revert if API call fails
        setInvites(prev => prev.map(invite => 
          invite.id === inviteId 
            ? { ...invite, inviteSentAt: error.originalInviteSentAt } 
            : invite
        ));
        
        console.error("Resend error:", error);
        message.error(error.response?.data?.message || "Failed to resend invitation");
      } finally {
        setResendingId(null);
      }
    },
    onCancel: () => setResendingId(null),
  });
};


const handleSendInvite = async (inviteData) => {
  try {
    const response = await sendInvite(inviteData);
    await getInvites();
    return { success: true, data: response };
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 400) {
        return { success: false, error: data };
      }
    }
    return { success: false, error: { message: error.message } }; 
  }
};

  useEffect(() => {
    getInvites();
  }, []);

  return {
    invites,
    loading,
    error,
    getInvites,
    handleResend,
    handleSendInvite,
    resendingId,
    pagination,
    handlePageChange,
  };
};
